/**
 * The Discover layout document (jorigin/brewlytics#2291): the caller's saved
 * searches and the sections their Discover page shows, at
 * `api/profiles/self/preferences/discover`. The Customize sheet (#2292)
 * reads and writes the same document.
 *
 * - GET answers the stored document, or the built-in default
 *   (`source: "default"`, `rev: 0`).
 * - PUT replaces the whole document against the `rev` it was read at.
 * - POST `…/reset` (#2298) puts a preset's sections and preset back (the
 *   default's when none is named) and keeps the saved searches.
 * - DELETE `?rev=N` (#2298) removes everything, saved searches too.
 *
 * Every write names the `rev` it read: a stale one is a 409 carrying the
 * server's current document, which each write here hands back as a conflict
 * rather than throwing, so a caller can show "changed on another device" and
 * reload.
 *
 * Every answer carries the server's registry (`registry`, #2298): the section
 * types, their variants and settings, the presets and the limits. The page
 * draws and checks a document with the registry it came with, and with
 * {@link BUILT_IN_REGISTRY} only before the first answer. The server checks
 * every field (a 400 names each problem, thrown as
 * {@link DiscoverLayoutInvalidError}) and is the authority on section types:
 * a section of a type this build does not know is kept as it came, never
 * edited, so it survives the round trip.
 */
import { isAxiosError } from 'axios';
import apiClient from './client';
import builtInRegistry from './discoverRegistry.json';

/** The schema version this build reads and writes. */
export const DISCOVER_SCHEMA = 2;

/** A setting's rule in the registry: the server's `Setting`. */
export type DiscoverSettingRule = { oneOrMoreOf: string[] } | { oneOf: string[] } | { searchRef: true };

/** A saved search's filter box (#2302): shown when it has more than the section holds, always, or never. */
export type FilterBoxMode = 'auto' | 'on' | 'off';

/** The registry as the server serves it (`DiscoverSections.registry()`). */
export interface DiscoverRegistry {
  limits: { count: number; sections: number; searches: number; title: number; description: number; bodyBytes: number };
  /** In registry order: the catalog's order. */
  types: { type: string; variants: string[]; settings: Record<string, DiscoverSettingRule> }[];
  /** The named presets, in the order the sheet offers them; `custom` is never listed. */
  presets: { name: string; sections: DiscoverSection[] }[];
}

/**
 * This build's copy of the server's registry, for the first frame (or no
 * answer at all) and for a document cached before #2298, which came without
 * one. `DiscoverSectionsTest` (ant unit-test) pins it to the server.
 */
export const BUILT_IN_REGISTRY: DiscoverRegistry = (() => {
  const { _source, ...registry } = builtInRegistry;
  return registry as DiscoverRegistry;
})();

/**
 * The server's limits, from this build's copy of the registry: what a form
 * checks before there is an answer. A document's own registry is what the
 * page checks it with (`registryOf`).
 */
export const DISCOVER_LIMITS = {
  searches: BUILT_IN_REGISTRY.limits.searches,
  sections: BUILT_IN_REGISTRY.limits.sections,
  title: BUILT_IN_REGISTRY.limits.title,
  description: BUILT_IN_REGISTRY.limits.description,
  count: { min: 1, max: BUILT_IN_REGISTRY.limits.count },
  bodyBytes: BUILT_IN_REGISTRY.limits.bodyBytes,
  /** A search's or a section's id. */
  id: /^[a-z0-9-]{1,32}$/,
} as const;

export type DiscoverPreset = 'default' | 'explorer' | 'builder' | 'operator' | 'custom';

/** The section types this build draws: the server's registry (DiscoverSections) serves which it accepts. */
export type DiscoverSectionType =
  | 'featured'
  | 'trending'
  | 'favorites'
  | 'editorsPicks'
  | 'continueEditing'
  | 'savedSearch'
  /** The caller's own scheduled tasks, with their last runs (#2303). */
  | 'scheduledTasks'
  /** The models colleagues shared with the caller, the latest updated first (#2303). */
  | 'sharedWithMe';

export type DiscoverVariant = 'hero' | 'carousel' | 'grid' | 'list';

/** A date range in a query: a relative window, or from and/or to (ISO). */
export type ModelQueryRange = { within: '7d' | '30d' | '90d' | '1y' } | { from?: string; to?: string };

/**
 * An advanced model search in `POST api/models/query`'s format (#2289), as a
 * saved search stores it. The server answers it in canonical form: every key
 * present, absent ones as null.
 */
export interface ModelQueryJson {
  v: 1;
  kind?: 'model';
  text?: string | null;
  tags?: { all?: string[]; any?: string[]; none?: string[] };
  /** `"me"` (the viewer, on every run) or profile ids. */
  authors?: string[];
  updated?: ModelQueryRange | null;
  created?: ModelQueryRange | null;
  editorsPick?: boolean;
  /** `"me"` (shared with the viewer) or a group id. */
  shared?: string | null;
  /** One functional: what the canonical form writes for `functionals: [[id]]`. */
  usesFunctional?: string | null;
  /** Groups of ids, (a and b) or c (#2299); present only when used. */
  functionals?: string[][] | null;
  submodels?: string[][] | null;
  sort?: 'relevance' | 'updated' | 'created' | 'name' | 'runs' | null;
}

/** A saved search. Its timestamps are the server's. */
export interface SavedSearch {
  id: string;
  /** Plain text, 1–60 characters, no `<`. */
  title: string;
  /** Plain text, up to 280 characters, no `<`. */
  description?: string;
  query: ModelQueryJson;
  createdAt?: string;
  updatedAt?: string;
}

export interface DiscoverSectionSettings {
  /** featured: what the carousel draws from. */
  sources?: ('editorsPicks' | 'favorites')[];
  /** favorites: which kinds. */
  kinds?: ('model' | 'document' | 'functional')[];
  /** savedSearch: a search in the same document. */
  searchId?: string;
  /** savedSearch: its filter box (#2302); absent is auto. */
  filterBox?: FilterBoxMode;
  [key: string]: unknown;
}

export interface DiscoverSection {
  id: string;
  /** A registry type, or one from another build: kept as it came, not rendered. */
  type: DiscoverSectionType | (string & {});
  variant: DiscoverVariant | (string & {});
  count: number;
  hidden?: boolean;
  /** Plain text, up to 60 characters, no `<`. */
  title?: string;
  settings?: DiscoverSectionSettings;
}

export interface DiscoverLayout {
  schema: typeof DISCOVER_SCHEMA;
  /** The revision this copy was read at: 0 for the default. */
  rev: number;
  preset: DiscoverPreset;
  updatedAt?: string;
  searches: SavedSearch[];
  sections: DiscoverSection[];
  /** Whether this is the profile's document or the built-in default. Never stored. */
  source?: 'stored' | 'default';
  /** The registry the server answered with (#2298). Never stored, never sent. */
  registry?: DiscoverRegistry;
}

/** What a write came to: stored, or refused because the document moved on since it was read. */
export type DiscoverPutResult =
  | { ok: true; layout: DiscoverLayout }
  | { ok: false; conflict: true; current: DiscoverLayout };

/** A 400: the server's problems, each naming its key (`sections[2].settings.searchId: …`). */
export class DiscoverLayoutInvalidError extends Error {
  readonly problems: string[];

  constructor(problems: string[]) {
    super(problems.length > 0 ? `Invalid Discover layout: ${problems.join('; ')}` : 'Invalid Discover layout');
    this.name = 'DiscoverLayoutInvalidError';
    this.problems = problems;
  }
}

const DISCOVER_URL = 'api/profiles/self/preferences/discover';

/** The caller's layout, or the built-in default. */
export async function getDiscoverPreferences(): Promise<DiscoverLayout> {
  const { data } = await apiClient.get<DiscoverLayout>(DISCOVER_URL);
  return data;
}

/**
 * Another profile's layout, for an administrator's support view: 404 to
 * anyone else.
 */
export async function getProfileDiscoverPreferences(profileId: string): Promise<DiscoverLayout> {
  const { data } = await apiClient.get<DiscoverLayout>(
    `api/profiles/${encodeURIComponent(profileId)}/preferences/discover`,
  );
  return data;
}

/**
 * A write's answer: a 409 resolves as a conflict with the server's current
 * document; a 400 throws {@link DiscoverLayoutInvalidError}; anything else
 * throws.
 */
async function written(write: () => Promise<{ data: DiscoverLayout }>): Promise<DiscoverPutResult> {
  try {
    const { data } = await write();
    return { ok: true, layout: data };
  } catch (err) {
    if (isAxiosError(err)) {
      const status = err.response?.status;
      const data = err.response?.data as { current?: DiscoverLayout; problems?: unknown } | undefined;
      if (status === 409 && data?.current) {
        return { ok: false, conflict: true, current: data.current };
      }
      if (status === 400) {
        const problems = Array.isArray(data?.problems) ? data.problems.map(String) : [];
        throw new DiscoverLayoutInvalidError(problems);
      }
    }
    throw err;
  }
}

/**
 * Replace the caller's whole layout. `layout.rev` must be the revision it was
 * read at.
 */
export async function putDiscoverPreferences(layout: DiscoverLayout): Promise<DiscoverPutResult> {
  // the document without the server's own fields (source, updatedAt, registry), which it ignores
  const { schema, rev, preset, searches, sections } = layout;
  const body = { schema, rev, preset, searches, sections };
  return written(() => apiClient.put<DiscoverLayout>(DISCOVER_URL, body));
}

/**
 * Reset the caller's layout to a preset's sections and preset (`default` when
 * none is named), keeping the saved searches, from the revision `rev` it was
 * read at.
 */
export async function resetDiscoverPreferences(rev: number, preset?: string): Promise<DiscoverPutResult> {
  return written(() => apiClient.post<DiscoverLayout>(`${DISCOVER_URL}/reset`, preset ? { rev, preset } : { rev }));
}

/**
 * Remove the caller's layout, saved searches and all, from the revision `rev`
 * it was read at: it answers the built-in default.
 */
export async function deleteDiscoverPreferences(rev: number): Promise<DiscoverPutResult> {
  return written(() => apiClient.delete<DiscoverLayout>(DISCOVER_URL, { params: { rev } }));
}
